import { useEffect, useState } from "react";
import { Eye, Save, Send, Undo2, X } from "lucide-react";
import { toast } from "sonner";
import { api, fmtError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { FieldInput } from "./FieldInput";
import Home from "../../pages/Home";

const GROUPS = [
  ["Hero banner", [
    { name: "hero_title", label: "Title" },
    { name: "hero_subtitle", label: "Subtitle", type: "textarea", rows: 2 },
    { name: "hero_image", label: "Hero banner image", type: "image", hint: "Wide landscape photo, at least 1600px wide." },
    { name: "hero_btn1_label", label: "Primary button label" },
    { name: "hero_btn1_link", label: "Primary button link (e.g. /events or https://...)" },
    { name: "hero_btn2_label", label: "Secondary button label" },
    { name: "hero_btn2_link", label: "Secondary button link" },
    { name: "player_development", label: "Player development (hero stat)" },
  ]],
  ["Welcome message", [
    { name: "welcome_title", label: "Welcome heading (leave empty to hide)" },
    { name: "welcome_message", label: "Welcome message", type: "textarea", rows: 4 },
  ]],
  ["Featured content", [
    { name: "news_count", label: "Number of latest news items to show (0 hides the section)", type: "number" },
    { name: "featured_event_ids", label: "Featured events (none selected = latest 3)", type: "refs", resource: "events", subKey: "status" },
    { name: "featured_bearer_ids", label: "Featured office bearers", type: "refs", resource: "bearers", labelKey: "name", subKey: "designation" },
    { name: "featured_player_ids", label: "Featured players", type: "refs", resource: "players", labelKey: "name", subKey: "playing_category" },
  ]],
  ["About cards", [
    { name: "card_coaching", label: "Coaching & player development", type: "textarea", rows: 2 },
    { name: "card_competitive", label: "Competitive badminton", type: "textarea", rows: 2 },
    { name: "card_community", label: "Community participation", type: "textarea", rows: 2 },
  ]],
  ["Call to action", [
    { name: "cta_title", label: "Heading" },
    { name: "cta_text", label: "Description" },
    { name: "cta_btn_label", label: "Button label" },
    { name: "cta_btn_link", label: "Button link" },
  ]],
];
const KEYS = GROUPS.flatMap(([, f]) => f.map((x) => x.name));
const pick = (o) => Object.fromEntries(KEYS.map((k) => [k, o[k]]));

export const HomepageEditor = () => {
  const { settings, refreshSettings } = useSite();
  const [form, setForm] = useState(null);
  const [draftAt, setDraftAt] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!settings) return;
    api.get("/settings/draft").then(({ data }) => {
      setForm(pick({ ...settings, ...(data || {}) }));
      setDraftAt(data?.saved_at || null);
    });
  }, [settings]);

  if (!form) return <p className="text-slate-500">Loading...</p>;
  const act = async (kind, fn, msg) => {
    setBusy(kind);
    try {
      await fn();
      toast.success(msg);
    } catch (err) {
      toast.error(fmtError(err));
    } finally {
      setBusy("");
    }
  };
  const saveDraft = () => act("draft", async () => { const { data } = await api.put("/settings/draft", form); setDraftAt(data.saved_at); }, "Draft saved (not yet live)");
  const publish = () => act("publish", async () => { await api.put("/settings", form); await api.delete("/settings/draft"); setDraftAt(null); await refreshSettings(); }, "Published — homepage is live");
  const discard = () => act("discard", async () => { await api.delete("/settings/draft"); setDraftAt(null); setForm(pick(settings)); }, "Draft discarded");

  return (
    <div data-testid="admin-homepage-editor">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-2 sticky top-0 z-10 bg-slate-100 py-3">
        <h2 className="font-display text-2xl font-bold text-[#0B1F44]">Homepage Content</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setPreview(true)} className="btn-outline text-sm" data-testid="homepage-preview-btn"><Eye className="h-4 w-4" /> Preview</button>
          <button onClick={saveDraft} disabled={!!busy} className="btn-outline text-sm" data-testid="homepage-save-draft-btn"><Save className="h-4 w-4" /> {busy === "draft" ? "Saving..." : "Save draft"}</button>
          <button onClick={publish} disabled={!!busy} className="btn-primary text-sm" data-testid="homepage-publish-btn"><Send className="h-4 w-4" /> {busy === "publish" ? "Publishing..." : "Publish"}</button>
        </div>
      </div>
      {draftAt && (
        <div className="mb-4 flex items-center justify-between rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="homepage-draft-banner">
          <span>You are editing an unpublished draft (saved {new Date(draftAt).toLocaleString("en-IN")}).</span>
          <button onClick={discard} className="inline-flex items-center gap-1 font-semibold hover:underline" data-testid="homepage-discard-draft-btn"><Undo2 className="h-3.5 w-3.5" /> Discard draft</button>
        </div>
      )}
      <p className="text-sm text-slate-500 mb-6">Edit, then use <b>Preview</b> to see the homepage before publishing. Nothing goes live until you click <b>Publish</b>.</p>
      <div className="space-y-6">
        {GROUPS.map(([title, fields]) => (
          <section key={title} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
            <h3 className="font-display text-lg font-semibold text-[#0B1F44]">{title}</h3>
            {fields.map((f) => (
              <div key={f.name}>
                <span className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</span>
                <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm({ ...form, [f.name]: v })} form={form} setForm={setForm} />
              </div>
            ))}
          </section>
        ))}
      </div>
      {preview && (
        <div className="fixed inset-0 z-[90] bg-slate-900/70 flex flex-col" data-testid="homepage-preview">
          <div className="flex items-center justify-between bg-[#0B1F44] text-white px-5 py-3">
            <span className="text-sm font-semibold">Preview — not published</span>
            <div className="flex gap-2">
              <button onClick={() => { setPreview(false); publish(); }} className="btn-primary text-sm !py-2" data-testid="homepage-preview-publish-btn"><Send className="h-4 w-4" /> Publish</button>
              <button onClick={() => setPreview(false)} className="btn-ghost-light text-sm !py-2" data-testid="homepage-preview-close"><X className="h-4 w-4" /> Close</button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-white"><Home preview={{ ...settings, ...form }} /></div>
        </div>
      )}
    </div>
  );
};
